import { useState, useEffect, useCallback } from 'react';
import ApiService from '../services/ApiService';

/**
 * Hook for managing notification state and badges
 * - Fetches notification counts from backend
 * - Provides counts by section for dashboard badges
 * - Handles marking notifications as read
 * - Polls for updates every 60 seconds
 */
const useNotifications = (isLoggedIn = false) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [sectionCounts, setSectionCounts] = useState({});
  const [prioritySection, setPrioritySection] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchNotificationCounts = useCallback(async () => {
    if (!isLoggedIn) {
      setUnreadCount(0);
      setSectionCounts({});
      setPrioritySection(null);
      return;
    }

    setLoading(true);
    try {
      const response = await ApiService.get('moderation/notifications/counts-by-section');
      if (response.data?.success) {
        const { counts, total, prioritySection: priority } = response.data.data;
        setUnreadCount(total || 0);
        setSectionCounts(counts || {});
        setPrioritySection(priority);
      }
    } catch (error) {
      console.error('Error fetching notification counts:', error);
      // Don't reset state on error - keep previous values
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  const markSectionAsRead = useCallback(async (section) => {
    if (!isLoggedIn || !section) return;

    // Optimistically update UI immediately
    setSectionCounts(prev => {
      const newCounts = { ...prev };
      if (newCounts[section]) {
        setUnreadCount(current => Math.max(0, current - newCounts[section]));
        newCounts[section] = 0;
      }
      return newCounts;
    });

    try {
      await ApiService.put('moderation/notifications/read-by-section', { section });
      // Refresh counts to sync with server
      await fetchNotificationCounts();
    } catch (error) {
      console.error('Error marking section as read:', error);
      // On error, refresh to get accurate counts
      fetchNotificationCounts();
    }
  }, [isLoggedIn, fetchNotificationCounts]);

  // Fetch on mount and when login status changes
  useEffect(() => {
    fetchNotificationCounts();
  }, [fetchNotificationCounts]);

  // Set up polling every 60 seconds when logged in
  useEffect(() => {
    if (!isLoggedIn) return;

    const interval = setInterval(fetchNotificationCounts, 60000);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchNotificationCounts]);

  return {
    // Total unread count (for bell badge)
    unreadCount,
    // Counts by section (for menu badges)
    sectionCounts,
    // Priority section to navigate to when bell is clicked
    prioritySection,
    // Loading state
    loading,
    // Refresh function
    refresh: fetchNotificationCounts,
    // Mark a section's notifications as read
    markSectionAsRead,
  };
};

export default useNotifications;
